import contractPage from '../hoc/contractPage/contractPage';
import LegalTermsAndPolicies from '../screens/LegalTermsAndPolicies/LegalTermsAndPolicies';
import { getBankTermsAndConditionsRecords } from '../service/cms/getTermsAndConditionsContent';
import { getBankPrivacyPoliciesRecords } from '../service/cms/getPrivacyPoliciesContent';

// In-memory cache for records fetched from DatoCMS.
// This avoids making repeated requests to DatoCMS during the build.
// Once getStaticPaths fetches all records, getStaticProps reuses them
// from memory instead of requesting them again for each page.
let cachedTermsRecords = null;
let cachedPrivacyRecords = null;

/**
 * Builds the page + seo props shape expected by LegalTermsAndPolicies from a
 * single DatoCMS record.
 */
function buildPageProps({
  record,
  pageNameSlug,
  informationKey,
  defaultTitle,
}) {
  if (!record) return null;

  const seo = record[informationKey]?.headSeo || {
    title: defaultTitle,
    description: '',
  };

  const page = [];

  const titleContent = record[informationKey]?.websiteTitle?.title;
  if (titleContent) {
    page.push({
      id: `${pageNameSlug}-title`,
      title: titleContent,
    });
  }

  const contentParagraph = record[informationKey]?.websiteContent?.multiLineParagraph;
  if (contentParagraph) {
    page.push({
      id: `${pageNameSlug}-content`,
      multiLineParagraph: contentParagraph,
    });
  }

  return {
    page,
    seo: {
      title: seo.title,
      description: seo.description,
      headSeoBreadcrumbTitle: seo.title,
    },
  };
}

/**
 * Fetches all legal records from DatoCMS (terms & conditions + privacy policies).
 *
 * The first time this function is called, it requests all records from DatoCMS
 * and stores them in memory. Every subsequent call returns the stored data
 * instantly without making any new requests.
 *
 * This means DatoCMS is only contacted once during the entire build,
 * no matter how many pages need to be generated.
 */
async function loadAllRecords() {
  if (cachedTermsRecords && cachedPrivacyRecords) {
    return {
      termsRecords: cachedTermsRecords,
      privacyPoliciesRecords: cachedPrivacyRecords,
    };
  }

  let termsRecords = cachedTermsRecords;
  let privacyPoliciesRecords = cachedPrivacyRecords;

  if (!termsRecords) {
    try {
      ({ termsRecords } = await getBankTermsAndConditionsRecords());
    } catch (error) {
      throw new Error(
        `Failed to fetch bank terms and conditions records from DatoCMS: ${error.message}`,
      );
    }
  }

  if (!privacyPoliciesRecords) {
    try {
      ({ privacyPoliciesRecords } = await getBankPrivacyPoliciesRecords());
    } catch (error) {
      throw new Error(
        `Failed to fetch bank privacy policies records from DatoCMS: ${error.message}`,
      );
    }
  }

  // If both lists are empty, something is wrong -- DatoCMS might be down
  // or returning no data. Fail the build with a clear message.
  if (!termsRecords?.length && !privacyPoliciesRecords?.length) {
    throw new Error(
      'Both terms and privacy records are empty. '
      + 'DatoCMS may be unreachable or returning no data.',
    );
  }

  cachedTermsRecords = termsRecords;
  cachedPrivacyRecords = privacyPoliciesRecords;

  return {
    termsRecords,
    privacyPoliciesRecords,
  };
}

/**
 * Generates the list of all legal page URLs at build time.
 *
 * Next.js calls this function once to know which pages to create.
 * For each record from DatoCMS, we generate a URL like:
 *   - /terminos-y-condiciones-plan-de-pagos
 *   - /aviso-de-privacidad-clientes-y-prospectos
 */
export async function getStaticPaths() {
  const { termsRecords, privacyPoliciesRecords } = await loadAllRecords();

  const termsPaths = termsRecords.map(record => ({
    params: { slug: `terminos-y-condiciones-${record.pageNameSlug}` },
  }));

  const privacyPaths = privacyPoliciesRecords.map(record => ({
    params: { slug: `aviso-de-privacidad-${record.pageNameSlug}` },
  }));

  const allPaths = [...termsPaths, ...privacyPaths];
  const uniquePaths = Array.from(
    new Map(allPaths.map(path => [path.params.slug, path])).values(),
  );

  // eslint-disable-next-line no-console
  console.log(
    `[getStaticPaths] Generated ${uniquePaths.length} legal page paths `
    + `(${termsPaths.length} terms, ${privacyPaths.length} privacy).`,
  );

  return {
    paths: uniquePaths,
    // fallback: false means only the pages listed above will exist.
    // Any other URL will return a 404. This is intentional -- we only
    // want pages that have actual content in DatoCMS.
    fallback: false,
  };
}

/**
 * Prepares the data for a single legal page at build time.
 *
 * Next.js calls this function once for each page URL generated by getStaticPaths.
 * It finds the matching record from our in-memory cache and extracts the
 * title, content, and SEO information needed to render the page.
 */
export async function getStaticProps({ params }) {
  const { slug } = params;

  const isTermsPage = slug.startsWith('terminos-y-condiciones-');
  const isPrivacyPage = slug.startsWith('aviso-de-privacidad-');

  if (!isTermsPage && !isPrivacyPage) {
    return { notFound: true };
  }

  const { termsRecords, privacyPoliciesRecords } = await loadAllRecords();

  let pageNameSlug;
  let record;
  let informationKey;
  let defaultTitle;

  if (isTermsPage) {
    pageNameSlug = slug.replace('terminos-y-condiciones-', '');
    record = termsRecords.find(r => r.pageNameSlug === pageNameSlug);
    informationKey = 'terminosYCondicionesInformation';
    defaultTitle = 'Términos y Condiciones';
  } else {
    pageNameSlug = slug.replace('aviso-de-privacidad-', '');
    record = privacyPoliciesRecords.find(r => r.pageNameSlug === pageNameSlug);
    informationKey = 'avisosDePrivacidadInformation';
    defaultTitle = 'Aviso de Privacidad';
  }

  if (!record) {
    // eslint-disable-next-line no-console
    console.error(
      `[getStaticProps] No record found for slug "${slug}" (pageNameSlug="${pageNameSlug}"). `
      + 'The record may have been removed from DatoCMS after getStaticPaths ran.',
    );
    return { notFound: true };
  }

  const bank = buildPageProps({
    record,
    pageNameSlug,
    informationKey,
    defaultTitle,
  });

  return {
    props: {
      bank,
      seo: bank.seo,
    },
  };
}

export default contractPage(LegalTermsAndPolicies, {
  routeKey: 'LEGAL_TERMS_AND_POLICIES',
  allowApplicationForm: false,
});
